import { type UserData } from "../constants/GlobalTypesAndEnumsAndConstants";
import { userStore } from "../zustand/globalStore";
import { getSubscriptionBillingIdentity } from "./subscription-billing";

/** The account and subscription owner used to choose a billing operation. */
export function getSubscriptionRequestKey(user: UserData | null | undefined): string {
  const subscription = user?.subscription_information;
  const billing = getSubscriptionBillingIdentity(subscription);
  return JSON.stringify([
    user?.id ?? null,
    billing.hasSubscriptionBinding ? subscription.subscription_id : null,
    billing.managerSubscriptionAccount ?? null,
  ]);
}

export class SubscriptionRequestCancelledError extends Error {
  constructor() {
    super("Subscription request is no longer current");
    this.name = "SubscriptionRequestCancelledError";
  }
}

/** Observe every store transition, including A→B→A hidden by React batching. */
export function createSubscriptionRequestAuthority(
  user: UserData | null | undefined,
  isCallerCurrent: () => boolean = () => true,
  onInvalidated?: () => void,
) {
  const key = getSubscriptionRequestKey(user);
  let invalidated = getSubscriptionRequestKey(userStore.getState().currentUser) !== key;
  const unsubscribe = userStore.subscribe((state) => {
    if (!invalidated && getSubscriptionRequestKey(state.currentUser) !== key) {
      invalidated = true;
      onInvalidated?.();
    }
  });
  const isCurrent = () => !invalidated && isCallerCurrent() &&
    getSubscriptionRequestKey(userStore.getState().currentUser) === key;
  return {
    isCurrent,
    assertCurrent() {
      if (!isCurrent()) throw new SubscriptionRequestCancelledError();
    },
    dispose() {
      invalidated = true;
      unsubscribe();
    },
  };
}
